import Button from '@mui/material/Button';
import { gridClasses } from '@mui/x-data-grid-premium';
import { PlayArrow } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { CircularProgress } from '@mui/material';
import FlowRunDisplay from '../FlowRunDisplay';
import { jobStatusColorMap } from '../utils';
import AQDataGrid from '../../AQDataGrid';
import { theme } from '../../../utils/theme';
import {
  Schedule,
  Extract,
  JobMutationData,
} from '../../../types/flowExtractsTable.types';
import { useGlobalSettings } from '../../../app/GlobalSettingProvider';
import { Flow, FlowRun, Model } from '../../../types/flow.types';
import { useJobMutation } from '../../../data/useJob';
import { useEntities, useEntity } from '../../../data/flowEntities';
import CronTableCellDisplay from '../CronTableCellDisplay';

interface FlowExtractsTableProps {
  flow: Model | Flow | null;
  currentAccount: { id: string };
  tabIsActive?: boolean;
}

const FlowExtractsTable = ({
  flow,
  currentAccount,
  tabIsActive,
}: FlowExtractsTableProps) => {
  const { settings } = useGlobalSettings();
  const createJobMutation = useJobMutation(currentAccount.id);
  const schedulesQuery = useEntities<Schedule[]>(
    currentAccount.id,
    'schedule',
    {
      filter: {
        entity_type: 'flow',
        entity_id: flow?.id,
      },
    },
    { enabled: Boolean(flow?.id) && tabIsActive },
  );
  const enableExtractsQuery =
    Boolean(flow?.['source']) && tabIsActive && Boolean(flow?.id);

  const extractsQuery = useEntity(
    currentAccount.id,
    'flow',
    `${flow?.id}/extracts`,
    {
      filter: {
        entity_type: flow?.['source']?.entity_type || '',
        entity_id: flow?.['source']?.id || '',
      },
      include: ['schema', 'stream'],
      enabled: enableExtractsQuery,
    },
  );

  const rowTransform = (schedules: Schedule[]) => (entities: Extract[]) =>
    entities
      .map((extract) => {
        const schedule = schedules.find(
          (schedule: Schedule) => schedule.id === extract.schedule_id,
        );
        const schema = entities.find(
          (extract_raw) => extract_raw.id === extract.id,
        )?.included?.schema?.[extract?.stream?.schema_id || ''];
        return {
          ...extract,
          schema,
          schema_id: extract?.stream?.schema_id,
          id: extract.id,
          schedule,
        };
      })
      .map((extract) => ({
        id: extract.id + (extract.schedule?.schedule || ''),
        name: extract.schedule?.title,
        extract_type: extract.extract_type,
        schedule: extract.schedule?.schedule,
        schedule_id: extract.schedule?.id,
        flow_id: flow?.id || '',
        objects: extract.schema?.relative_path,
      }));

  return (
    <AQDataGrid
      dataDependencies={[schedulesQuery, extractsQuery]}
      dataQueryHook={useEntity}
      buildQueryArgs={() => [
        currentAccount.id,
        'flow',
        `${flow?.id}/extracts`,
        {
          filter: {
            entity_type: flow?.['source']?.entity_type || '',
            entity_id: flow?.['source']?.id || '',
          },
          include: ['schema', 'stream'],
          enabled: enableExtractsQuery,
        },
      ]}
      rowsTransformer={rowTransform(schedulesQuery.data?.getData() || [])}
      colDefs={[
        {
          field: 'schedule',
          headerName: 'Extract',
          width: 400,
          groupingValueGetter: (groupingData: any) => groupingData,
          renderCell: (params: { value: any }) => (
            <CronTableCellDisplay
              sx={{ fontWeight: 900 }}
              params={{ value: { schedule: params.value } }}
            />
          ),
        },
        {
          field: 'objects',
          headerName: 'Objects',
          flex: 1,
          sortable: false,
        },
        {
          field: 'extract_type',
          headerName: 'Extract Type',
          flex: 1,
          sortable: false,
        },
        {
          field: 'name',
          headerName: 'Title',
          flex: 1,
          sortable: false,
        },
        {
          field: 'actions',
          headerName: 'Actions',
          flex: 1,
          sortable: false,
          renderCell: (params: { id: string; rowNode: { type: string } }) => {
            const { rowNode } = params;

            if (rowNode && rowNode.type === 'group') {
              const schedule = schedulesQuery.data
                ?.getData()
                .find(
                  (sched: Schedule) =>
                    sched.schedule === params.id.split('schedule/')[1],
                );

              const mutationData =
                createJobMutation?.data as unknown as FlowRun;
              if (
                createJobMutation.isSuccess &&
                mutationData &&
                mutationData.triggered_by_id === schedule?.id
              ) {
                return (
                  <a
                    href={`/${(currentAccount as { id: string; name?: string }).name ?? ''}/flows/${flow?.['name']}/syncs/${mutationData.id}`}
                    style={{
                      textDecoration: 'none',
                      color: theme.palette.primary.main,
                      fontWeight: 'bold',
                    }}
                  >
                    <FlowRunDisplay
                      timezone={settings.timeZone.current}
                      nextOrLastRun="next"
                      flowRun={mutationData}
                    />
                  </a>
                );
              }

              if (createJobMutation.isError) {
                return (
                  <span style={{ display: 'flex', alignItems: 'center' }}>
                    {/* @ts-expect-error: CloseIcon small prop is not typed in MUI */}
                    <CloseIcon small htmlColor={jobStatusColorMap.failed} />
                    <small style={{ marginLeft: theme.spacing(1) }}>
                      Sync creation failed
                    </small>
                  </span>
                );
              }

              if (createJobMutation.isPending) {
                return (
                  <span style={{ display: 'flex', alignItems: 'center' }}>
                    <CircularProgress size={20} />
                    <small style={{ marginLeft: theme.spacing(1) }}>
                      Creating Sync
                    </small>
                  </span>
                );
              }

              return (
                <Button
                  color="primary"
                  startIcon={<PlayArrow />}
                  variant="outlined"
                  onClick={() => {
                    if (!flow?.id || !schedule?.id) return;
                    const data: JobMutationData = {
                      entity_type: 'flow',
                      job_type: 'flow',
                      entity_id: flow.id,
                      triggered_by_id: schedule.id,
                    };
                    // @ts-expect-error: createJobMutation is not typed correctly
                    createJobMutation.mutate(data);
                  }}
                >
                  Sync Now
                </Button>
              );
            }
            return null;
          },
        },
      ]}
      sx={{
        [`& .${gridClasses.menuIcon}`]: {
          visibility: 'visible',
          width: 'auto',
        },
      }}
      gridProps={{
        initialState: {
          columns: {
            columnVisibilityModel: {
              schedule: false,
            },
          },
          rowGrouping: {
            model: ['schedule'],
          },
        },
        defaultGroupingExpansionDepth: -1,
      }}
    />
  );
};

export default FlowExtractsTable;
